import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { usePageTitle } from '../hooks/usePageTitle'
import { api } from '../lib/api'
import {
  BrassRule,
  ClubButton,
  ClubLinkButton,
  Container,
  Crest,
  Eyebrow,
  JoinCard,
  JoinPage,
  PromotionCodeField,
  type ResolvedPromotion,
} from '../components/golf'
// By path, not from the barrel: it imports @stripe/react-stripe-js, and the
// barrel is pulled in by every club screen and by clubPages.test.tsx.
import { DuesPaymentForm } from '../components/golf/DuesPaymentForm'
import { cn } from '../lib/cn'
import { clubReturnUrl } from '../lib/stripe'
import {
  DEFAULT_TIER_ID,
  MEMBERSHIP_BILLING_ENABLED,
  findTier,
  membershipTiers,
} from '../lib/membership'
import type { ClubSubscription } from '../lib/types'

/**
 * Settlement of dues (route map §4 `/join/checkout`) — step II.
 *
 * The card is collected HERE, in the club's own page, rather than on Stripe's.
 * The PAN still never enters our DOM — the Payment Element is a Stripe-hosted
 * iframe, so the account stays at SAQ-A exactly as it did under hosted
 * Checkout. What we gain is the chrome, the copy and the coupon field.
 *
 * Two phases. Phase one is the terms and the member's code; pressing through
 * creates the subscription server-side. Phase two is the card — but ONLY when
 * the server says one is needed:
 *
 *   A fully-discounted standing has no PaymentIntent, no client secret and is
 *   active the moment it is created. Branch on `payment_required`, never on
 *   `client_secret` being null and never on the amount being zero — this is the
 *   path every beta code takes, and rendering a card form for it would ask
 *   someone to pay nothing.
 *
 * While `Fireside::Club::FeatureFlag` is off the CTA says so rather than
 * leading somewhere that cannot take payment. The order summary still renders
 * in full — this page is a real preview of the terms either way.
 */
export function JoinCheckoutScreen() {
  usePageTitle('Settle your dues')
  const search = useSearch({ strict: false }) as { tier?: string }
  const navigate = useNavigate()
  const founding = findTier('founding')!

  // The arriving ?tier= is a PRESELECTION, not the answer. It used to be the
  // answer, with `membershipTiers()[1]` behind it when nothing arrived — which
  // meant someone who clicked "Claim a founding seat" was quietly shown Full
  // Member and charged for it. The choice cannot always survive the journey
  // (the emailed link may be opened on another device), so it is asked here
  // instead of assumed. Positional indexing is gone with it: index 1 was Full
  // only because the Ruby array happens to be ordered that way, so reordering
  // tiers.rb would have silently changed the price.
  const [chosen, setChosen] = useState<string | undefined>(findTier(search?.tier)?.id)
  const tier = findTier(chosen) ?? findTier(DEFAULT_TIER_ID)!

  const [promotion, setPromotion] = useState<ResolvedPromotion | null>(null)
  const [subscription, setSubscription] = useState<ClubSubscription | null>(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const applyPromotion = async (code: string) => {
    const { promotion: resolved } = await api.post<{ promotion: ResolvedPromotion }>(
      '/club/promotion_code',
      { code, tier: tier.id },
    )
    setPromotion(resolved)
    return resolved
  }

  const startSubscription = async () => {
    setStarting(true)
    setError(null)
    try {
      const result = await api.post<ClubSubscription>('/club/subscription', {
        tier: tier.id,
        promotion_code: promotion?.code,
      })
      if (!result.payment_required) {
        // Nothing to confirm — the standing is already live. Straight to
        // arrival, which syncs it and shows the register number.
        navigate({ to: '/join/welcome', search: { subscription_id: result.subscription_id } })
        return
      }
      setSubscription(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't reach the front desk. Try again?")
    } finally {
      setStarting(false)
    }
  }

  const included = tier.features.filter((feature) => feature.included)
  const period = tier.interval === 'mo' ? 'month' : 'year'
  const fullAmount = `${tier.price}.00`
  // The server's number wins whenever there is one: a promotion preview, then
  // the amount Stripe actually put on the invoice.
  const dueCents = subscription?.amount_due_cents ?? promotion?.amount_due_cents ?? null
  const dueAmount = dueCents === null ? fullAmount : `$${(dueCents / 100).toFixed(2)}`
  const discounted = dueAmount !== fullAmount

  return (
    <JoinPage stepNumeral="II" stepLabel="The Dues">
      <Container className="max-w-[1064px] px-0">
        <div className="mb-11 text-center">
          <Eyebrow>Membership · Settlement of Dues</Eyebrow>
          <h1 className="mt-3.5 font-display text-[clamp(2rem,4vw,2.625rem)] font-medium leading-[1.04] text-pine">
            Settle your dues.
          </h1>
          <p className="mt-2.5 text-[15px] text-ink-soft">
            A brief formality at the front desk, and the grounds are yours.
          </p>
        </div>

        <div className="grid items-start gap-11 lg:grid-cols-[440px_minmax(0,1fr)] lg:gap-15">
          {/* ── Your standing ── */}
          <JoinCard as="aside" className="max-w-none px-9 pb-8 pt-10">
            <Eyebrow className="text-[11px]">Your standing</Eyebrow>
            <p className="mt-2.5 font-display text-[27px] text-pine">{tier.name}</p>
            <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
              In good standing from today
            </p>

            {/*
              The member says which standing this is, on the page that charges
              for it. It used to be inferred — ?tier= if it survived the journey,
              a positional default if it did not — so clicking "Claim a founding
              seat" could quietly end in Full Member. The choice cannot always
              travel (the emailed link may be opened on another device), so the
              honest move is to ask rather than to guess.
            */}
            {!subscription && (
              <fieldset className="mt-5 border-t border-brass/35 pt-4.5">
                <legend className="sr-only">Choose your standing</legend>
                <div className="flex flex-col gap-2">
                  {membershipTiers().map((option) => (
                    <label
                      key={option.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-club-sm border px-3.5 py-2.5 text-[13.5px] transition-colors',
                        option.id === tier.id
                          ? 'border-brass bg-sage-soft/30 text-pine'
                          : 'border-brass/35 text-ink hover:border-brass/60',
                      )}
                    >
                      <input
                        type="radio"
                        name="tier"
                        value={option.id}
                        checked={option.id === tier.id}
                        onChange={() => setChosen(option.id)}
                        disabled={starting}
                        className="size-3.5 accent-fairway"
                      />
                      <span className="flex-1 font-semibold">{option.name}</span>
                      <span className="font-display tabular-nums text-pine">
                        {option.price}
                        <span className="font-text text-[11px] text-ink-soft">
                          {' '}
                          / {option.interval}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <p className="mt-4 font-display text-[40px] leading-none text-pine">
              {tier.price}
              <span className="align-baseline font-text text-[13px] font-medium text-ink-soft">
                {' '}
                / {period}
              </span>
            </p>

            <ul className="my-5.5 flex flex-col gap-2.5 border-t border-brass/35 pt-5">
              {included.map((feature) => (
                <li key={feature.label} className="relative pl-6 text-sm text-ink">
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-[7px] size-[9px] rounded-full border-[1.5px] border-brass bg-brass"
                  />
                  {feature.label}
                </li>
              ))}
            </ul>

            <dl className="border-t border-brass pt-3.5">
              <div className="mb-1.5 flex items-baseline justify-between text-[13.5px] text-ink-soft">
                <dt>{tier.interval === 'mo' ? 'Monthly' : 'Annual'} dues</dt>
                <dd className="font-display tabular-nums text-[15px] text-pine">{fullAmount}</dd>
              </div>
              {discounted && promotion && (
                <div className="mb-1.5 flex items-baseline justify-between text-[13.5px] text-ink-soft">
                  <dt>{promotion.code}</dt>
                  <dd className="font-display tabular-nums text-[15px] text-pine">
                    {promotion.summary}
                  </dd>
                </div>
              )}
              <div className="mt-2 flex items-baseline justify-between border-t border-brass/35 pt-3 text-xs font-semibold uppercase tracking-[0.16em] text-ink">
                <dt>Due today</dt>
                <dd className="font-display text-[26px] normal-case tracking-normal tabular-nums text-pine">
                  {dueAmount}
                </dd>
              </div>
            </dl>

            {MEMBERSHIP_BILLING_ENABLED && !subscription && (
              <PromotionCodeField
                applied={promotion}
                onApply={applyPromotion}
                onRemove={() => setPromotion(null)}
                disabled={starting}
              />
            )}

            {tier.id !== 'founding' && (
              <div className="mt-5.5 flex items-center gap-3.5 rounded-club border border-brass/45 bg-sage-soft/28 px-4 py-3.5">
                <Crest className="size-8 shrink-0" />
                <p className="flex-1 text-[12.5px] leading-[1.45] text-ink-soft">
                  <b className="font-semibold text-pine">The founding seal</b> — {founding.price}
                  &nbsp;/&nbsp;yr, rate held for life. A hundred are struck in total.
                </p>
              </div>
            )}
          </JoinCard>

          {/* ── The particulars ── */}
          <section className="pt-1">
            <Eyebrow className="text-[11px]">Payment</Eyebrow>
            <h2 className="mb-6 mt-2.5 font-display text-[27px] font-medium text-pine">
              The particulars.
            </h2>

            {!MEMBERSHIP_BILLING_ENABLED && (
              <>
                <BrassRule short className="mb-5" />
                <p className="mb-3 max-w-[46ch] text-[14.5px] text-ink-soft">
                  The front desk is not yet taking dues. The club is open to walk about in the
                  meantime — the grounds, the register and the lounge are all unlocked, and your
                  sign-in works today.
                </p>
                <p className="mb-6 max-w-[46ch] text-[14.5px] text-ink-soft">
                  Apply now and we&rsquo;ll write to you the moment standings can be settled.
                </p>
                <ClubLinkButton to="/join" className="w-full">
                  Apply for membership
                </ClubLinkButton>
              </>
            )}

            {MEMBERSHIP_BILLING_ENABLED && !subscription && (
              <>
                <p className="mb-6 max-w-[46ch] text-[14.5px] text-ink-soft">
                  Card details are taken by Stripe inside this page, never by the club — we never
                  see or hold a card number.
                </p>
                {error && (
                  <p role="alert" className="mb-4 max-w-[46ch] text-[13.5px] text-flag">
                    {error}
                  </p>
                )}
                <ClubButton className="w-full" onClick={startSubscription} disabled={starting}>
                  {starting ? 'Opening the desk…' : 'Settle your dues'}
                </ClubButton>
              </>
            )}

            {MEMBERSHIP_BILLING_ENABLED && subscription?.client_secret && (
              <DuesPaymentForm
                clientSecret={subscription.client_secret}
                returnUrl={clubReturnUrl(subscription.subscription_id)}
                onConfirmed={() =>
                  navigate({
                    to: '/join/welcome',
                    search: { subscription_id: subscription.subscription_id },
                  })
                }
                onCancel={() => setSubscription(null)}
              />
            )}

            <p className="mt-4 flex items-center justify-center gap-2 text-[12.5px] text-ink-soft">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-brass)"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
                className="size-3 shrink-0"
              >
                <rect x="4" y="10" width="16" height="10" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
              Settled securely via Stripe. Cancel your standing anytime.
            </p>
          </section>
        </div>
      </Container>
    </JoinPage>
  )
}
